import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useMemo } from 'react';
import { numericRatingOptions } from 'src/ratings/bandedRatingLookups';
import { useScoringSettings } from 'src/ratings/useScoringSettings';

import { hasLikelihoodImpact } from '@/utils/utils';

import type { RatingCellOptionWithColour } from './ratingBandRegions';

/**
 * The matrix cells the organisation rates against, from whichever source is
 * live: the scoring config when `scoring_settings_data` is on, the platform
 * taxonomy otherwise. The same selection `useCalculateRiskRating` makes, but
 * returned as data so the band regions can be derived from the shared rule
 * over the declared coordinates rather than from cell-by-cell lookups.
 */
export const useRatingCellOptions = (
  controlType: RiskAssessmentResultControlType
): RatingCellOptionWithColour[] => {
  const { hasScoringSettings, matrixOptions } = useScoringSettings({
    configType: 'risk_assessment',
  });
  const { options: taxonomyOptions } = useRating(
    controlType === RiskAssessmentResultControlType.Controlled
      ? 'risk_controlled'
      : 'risk_uncontrolled'
  );

  return useMemo(
    () =>
      hasScoringSettings
        ? matrixOptions
        : numericRatingOptions(taxonomyOptions.filter(hasLikelihoodImpact)),
    [hasScoringSettings, matrixOptions, taxonomyOptions]
  );
};
